import type { LiveModel } from '../adapter'
import { StreamPreview } from '../StreamPreview'
import { videoLabel } from '../setup'
import { driftText, driftWarn, type PairView } from '../vertical'
import { t } from '../../i18n'
import { Button, Icon, Tag } from '../../ui'

/** Video of the vertical input: 1080×1920 30 (a portrait size is never "1920p"). */
export function verticalVideo(resolution?: string, fps?: number): string | undefined {
  if (!resolution) return undefined
  const m = /^(\d+)x(\d+)$/.exec(resolution)
  const f = fps ? ` ${Math.round(fps)}` : ''
  return m ? `${m[1]}×${m[2]}${f}` : `${resolution}${f}`
}

const mbps = (v?: number) => (v !== undefined ? `${v.toFixed(1)} Mbps` : '—')

/**
 * The vertical input's preview next to the 16:9 stage, the same height (mockup A, F, G; VT-01, VT-13):
 * on air — the picture, a LIVE chip and two numbers stacked; on air before the main input — the picture
 * with WAITING FOR MAIN; not here yet — a dashed box that listens.
 */
export function VerticalPreview({ pair, now, phone }: { pair: PairView; now: number; phone?: boolean }) {
  const state = pair.live ? (pair.mainLive ? 'live' : 'ready') : 'waiting'
  const label = `${t('vert.preview.label')}${pair.live && pair.resolution ? ` — ${verticalVideo(pair.resolution, pair.fps)}` : ''}`
  return (
    // DS: VT-01 — .sx-preview in 9:16 (the system has the 16:9 frame only), HUD of two numbers stacked
    <section data-testid="vertical-preview" data-state={state} aria-label={label}
      className="sx-preview relative w-full min-w-0" style={{ aspectRatio: '9 / 16' }}>
      {pair.preview && state !== 'waiting' ? (
        <div className="absolute inset-0"><StreamPreview stream={pair.preview.stream} available={pair.preview.available} now={now} width={180} height={320} fluid aspect="9 / 16" plain /></div>
      ) : state !== 'waiting' ? <div className="absolute inset-0 bg-bg" /> : null}
      {state === 'waiting' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 border border-dashed border-line-2 bg-bg p-2 text-center" style={{ borderRadius: 'inherit' }}>
          <Icon name="signal" size={16} className="text-muted" />
          <span className="font-mono text-[11px] tracking-[.12em] text-muted">{t('vert.f.listening').toUpperCase()}</span>
        </div>
      )}
      {state === 'live' && !phone && (
        // VT-13: LIVE as .sx-chip.live.sm, still (the header logo is the view's heartbeat)
        <span className="tl"><span className="sx-chip live sm" data-testid="vertical-preview-chip"><Icon name="state-live" size={12} />{t('bar.live')}</span></span>
      )}
      {state === 'ready' && (
        // DS: VT-13 — the words of a vertical input that came before the main one
        <span className="tl"><span className="sx-chip idle sm" data-testid="vertical-preview-chip"><Icon name="state-idle" size={12} />{t('vert.f.readyChip')}</span></span>
      )}
      {state !== 'waiting' && !phone && (
        <div className="hud !flex-col !items-start !gap-1">
          <div><span>{t('strip.bitrate')}</span><b className="num">{mbps(pair.bitrateMbps)}</b></div>
          <div><span>{t('strip.video')}</span><b className="num">{verticalVideo(pair.resolution, pair.fps) ?? '—'}</b></div>
        </div>
      )}
    </section>
  )
}

/**
 * Two inputs under the stage (mockup A, VT-16): one row per input — Main and Vertical 9:16 — with how it comes in,
 * bitrate, video, keyframes, then RTT (main) or the drift against main (vertical; warn over 1 s), and how many
 * outputs it feeds; the button of a row opens the Input panel (OBS settings, the Vertical input block in it).
 */
export function InputPair({ m, pair, phone, open, onOpen }: { m: LiveModel; pair: PairView; phone?: boolean; open?: boolean; onOpen: () => void }) {
  const s = m.summary
  const src = m.source
  const live = s.live && !src.unknown
  const mainOuts = m.dests.filter((d) => d.enabled && (!d.vertical || d.vertical.state === 'fallback')).length
  const warn = driftWarn(pair.driftMs)
  const rows: { id: 'main' | 'vertical'; who: string; tag?: boolean; cells: [string, string, boolean?][] }[] = [
    {
      id: 'main', who: t('vert.row.main'), cells: [
        [t('vert.sum.input'), src.protocol?.toUpperCase() ?? '—'],
        [t('vert.sum.bitrate'), live ? mbps(s.bitrateMbps) : '—'],
        [t('vert.sum.video'), live ? videoLabel(s.resolution, s.fps) ?? '—' : '—'],
        [t('vert.sum.keyframes'), live && s.keyframeSec ? `${s.keyframeSec.toFixed(1)} s` : '—'],
        // the mockup's "Dropped" has no field in the live view yet (question in the report): RTT, as the input line
        [t('strip.rtt'), live && src.delay && src.delay.rttMs > 0 ? `${Math.round(src.delay.rttMs)} ms` : '—'],
        [t('vert.sum.outputs'), String(mainOuts)],
      ],
    },
    {
      id: 'vertical', who: t('vert.row.vertical'), tag: true, cells: [
        [t('vert.sum.input'), pair.protocol?.toUpperCase() ?? '—'],
        [t('vert.sum.bitrate'), mbps(pair.bitrateMbps)],
        [t('vert.sum.video'), verticalVideo(pair.resolution, pair.fps) ?? '—'],
        [t('vert.sum.keyframes'), pair.keyframeSec ? `${pair.keyframeSec.toFixed(1)} s` : '—'],
        [t('vert.sum.drift'), driftText(pair.driftMs), warn],
        [t('vert.sum.outputs'), String(pair.outputs)],
      ],
    },
  ]
  if (phone) {
    // VT-14: two compact rows under the previews — who, video, bitrate
    return (
      <div data-testid="input-pair" className="flex flex-col rounded-lg border border-line bg-panel">
        {rows.map((r) => (
          <button key={r.id} type="button" data-testid={`input-pair-${r.id}`} aria-expanded={open} onClick={onOpen}
            className="flex min-h-11 items-center gap-2 px-3 text-left [&+&]:border-t [&+&]:border-line">
            <span className="flex min-w-0 flex-1 items-center gap-2 text-[13px] font-medium">{r.who}{r.tag && <Tag>{t('vert.tag')}</Tag>}</span>
            <span className="num font-mono text-xs text-text-soft">{r.cells[2]![1]}</span>
            <span className="num font-mono text-xs text-text">{r.cells[1]![1]}</span>
          </button>
        ))}
      </div>
    )
  }
  return (
    <div data-testid="input-pair" className={`flex w-full flex-none flex-col rounded-lg border bg-panel ${open ? 'border-accent' : 'border-line'}`}>
      {rows.map((r) => (
        <div key={r.id} data-testid={`input-pair-${r.id}`} className="grid min-h-14 grid-cols-[150px_minmax(0,.8fr)_minmax(0,1fr)_minmax(0,1.25fr)_minmax(0,.9fr)_minmax(0,1.1fr)_minmax(0,.7fr)_32px] items-center gap-x-3.5 px-4 py-2.5 [&+&]:border-t [&+&]:border-line">
          <span className="flex items-center gap-2 text-sm font-medium">{r.who}{r.tag && <Tag>{t('vert.tag')}</Tag>}</span>
          {r.cells.map(([k, v, w]) => (
            <span key={k} className="flex min-w-0 flex-col gap-0.5">
              <span className="sx-label truncate">{k}</span>
              <span data-testid={k === t('vert.sum.drift') ? 'pair-drift' : undefined} data-state={w ? 'warn' : undefined}
                className={`num inline-flex items-center gap-1 truncate font-mono text-[15px] leading-5 font-medium ${w ? 'text-warn' : ''}`}>
                {w && <Icon name="state-warn" size={12} />}{v}
              </span>
            </span>
          ))}
          <Button variant="ghost" size="sm" icon onClick={onOpen} aria-expanded={open}
            aria-label={r.id === 'main' ? t('vert.sum.mainSettings') : t('vert.sum.settings')} title={r.id === 'main' ? t('vert.sum.mainSettings') : t('vert.sum.settings')}>
            <Icon name="tune" size={16} />
          </Button>
        </div>
      ))}
    </div>
  )
}
